/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Typography,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  useTheme,
  useMediaQuery,
  InputAdornment,
  SelectChangeEvent,
  Skeleton,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import * as Sentry from "@sentry/react";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { RootState, useAppDispatch } from "@store/store";
import {
  ApiError,
  GuaranteeConfigStruture,
  PayloadRequestGarantias,
  PayloadRequestGarantiasVehicularInmobiliaria,
  StatusCatalog,
  VehicleStrutureCatalog,
} from "@interfaces/index";
import {
  useLazyGetVehiclesByYearAndMakeQuery,
  useLazyGetVehiclesByYearMakeAndModelQuery,
  useLazyGetVehiclesByYearQuery,
  useLazyGetVehiclesYearsQuery,
} from "@api/catalogs";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  useDeleteGuaranteesTypesMutation,
  useLazyGetGuaranteesDataQuery,
  usePatchGuaranteesTypesMutation,
  usePostGuaranteesTypesMutation,
} from "@api/garantiasApi";
import { usePostValidateNextStageMutation, usePutApplicationCompletedMutation } from "@api/applications";
import { useNavigate } from "react-router-dom";
import { customStylesAsterisk } from "@components/customStylesInputs/inputsStyles";
import { setIsDisabledGuaranteeTypeSelect } from "@store/slices/registerSlice";

interface VehicleProps {
  type_vehicle: string;
  year: string;
  brand: string;
  brand_value: string;
  model: string;
  model_value: string;
  version: string;
  version_value: string;
  km: string;
  estimated_value: string;
  id?: string;
}

interface FormGarantiaVehicularProps {
  nextStatus: StatusCatalog | null;
  guarantee_type_id: string | null;
  guaranteeConfigList: GuaranteeConfigStruture[];
  onSeleccion: (guarantee_type: string) => void;
  guaranteeLabel?: string;
  canAdd?: boolean;
}

const vehiculos_manual = [
  "Vans y Last mile delivery",
  "Tractocamiones",
  "Camionetas",
  "Maquinaria amarilla",
  "Montacargas",
  "Camionetas",
];

const FormGarantiaVehicular = ({
  nextStatus,
  guarantee_type_id,
  guaranteeConfigList,
  onSeleccion,
  guaranteeLabel,
}: FormGarantiaVehicularProps) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const isDisabled = useSelector((state: RootState) => state.register.is_disabled_guarantee_type);

  const [triggerListVehicleYears] = useLazyGetVehiclesYearsQuery();
  const [triggerGetVehicleByYear] = useLazyGetVehiclesByYearQuery();
  const [triggerGetVehicleByYearAndBrand] = useLazyGetVehiclesByYearAndMakeQuery();
  const [triggerGetVehicleByYearBrandAndModel] = useLazyGetVehiclesByYearMakeAndModelQuery();
  const [postGuarantees] = usePostGuaranteesTypesMutation();
  const [getGuaranteesData] = useLazyGetGuaranteesDataQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [putApplicationCompleted] = usePutApplicationCompletedMutation();
  const [patchGuarantees] = usePatchGuaranteesTypesMutation();
  const [deleteGuarantees] = useDeleteGuaranteesTypesMutation();

  const emptyVehicle: VehicleProps = {
    type_vehicle: "",
    year: "",
    brand: "",
    brand_value: "",
    model: "",
    model_value: "",
    version: "",
    version_value: "",
    km: "",
    estimated_value: "",
    id: undefined,
  };

  const [brandsOptions, setBrandsOptions] = useState<Record<number, VehicleStrutureCatalog[]>>([]);
  const [modelsOptions, setModelsOptions] = useState<Record<number, VehicleStrutureCatalog[]>>([]);
  const [versionsOptions, setVersionsOptions] = useState<Record<number, VehicleStrutureCatalog[]>>([]);
  const [listYears, setListYears] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [loadingBrand, setLoadingBrand] = useState<{ [index: number]: boolean }>({});
  const [loadingModel, setLoadingModel] = useState<{ [index: number]: boolean }>({});
  const [loadingVersion, setLoadingVersion] = useState<{ [index: number]: boolean }>({});

  const formik = useFormik({
    initialValues: {
      vehicles: [{ ...emptyVehicle }],
      guarantee_type: guarantee_type_id || "",
    },
    validationSchema: yup.object().shape({
      vehicles: yup.array().of(
        yup.object().shape({
          type_vehicle: yup.string().required("Campo requerido"),
          year: yup.string().required("Campo requerido"),
          brand: yup.string().required("Campo requerido"),
          model: yup.string().required("Campo requerido"),
          version: yup.string().required("Campo requerido"),
          km: yup.string().required("Campo requerido"),
          estimated_value: yup.string().when("type_vehicle", {
            is: (value?: string) => vehiculos_manual.includes(String(value ?? "")),
            then: (schema) => schema.required("Campo requerido"),
            otherwise: (schema) => schema.notRequired(),
          }),
        }),
      ),
      guarantee_type: yup.string().required("Campo requerido"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setIsLoading(true);
        setSubmitting(true);

        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        const guaranteesToPatch: Array<{ guarantee_id: string; data: PayloadRequestGarantiasVehicularInmobiliaria }> =
          [];
        const guaranteesToPost: PayloadRequestGarantiasVehicularInmobiliaria[] = [];

        values.vehicles.forEach((vehicle) => {
          const cleanKm = Number(vehicle.km.replace(/,/g, "")) || 0;
          const cleanEstimatedValue = Number(String(vehicle.estimated_value ?? "").replace(/,/g, "")) || 0;
          const tracto = vehiculos_manual.includes(vehicle.type_vehicle);

          const guaranteeItem: PayloadRequestGarantiasVehicularInmobiliaria = {
            loan_application_id: application.id,
            guarantee_type_id: values.guarantee_type,
            estimated_value: tracto ? cleanEstimatedValue : null,
            details: {
              vehicle_type: vehicle.type_vehicle || "",
              year: Number(vehicle.year) || 0,
              make: vehicle.brand_value || "",
              model: vehicle.model_value || "",
              version: vehicle.version_value || "",
              car_version_code: tracto ? null : String(vehicle.version),
              estimated_mileage: cleanKm,
            },
          };

          if (vehicle?.id) {
            guaranteesToPatch.push({ guarantee_id: vehicle.id, data: guaranteeItem });
          } else {
            guaranteesToPost.push(guaranteeItem);
          }
        });

        await Promise.all([
          ...guaranteesToPatch.map(({ guarantee_id, data }) =>
            patchGuarantees({ loan_application_id: application.id, guarantee_id, data }).unwrap(),
          ),
          guaranteesToPost.length > 0
            ? postGuarantees({
                loan_application_id: application.id,
                data: { guarantees_list: guaranteesToPost } as PayloadRequestGarantias,
              }).unwrap()
            : Promise.resolve(),
        ]);

        await putApplicationCompleted({
          id_sol: application.id,
          body: { guarantee_types: [values.guarantee_type] },
        }).unwrap();

        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus?.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();
        dispatch(setIsDisabledGuaranteeTypeSelect({ disabled: true }));
        navigate(nextStatus?.url);
      } catch (error) {
        const api_error = error as ApiError;

        Sentry.captureException(error, {
          extra: {
            component: "FormGarantiaVehicular-submit",
            values: formik.values,
          },
        });

        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
        setIsLoading(false);
      }
    },
  });

  const handleChangeEstimatedValue = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, "");
    const numericValue = Number(rawValue) || 0;
    const formattedValue = rawValue ? numericValue.toLocaleString("en-US") : "";
    formik.setFieldValue(`vehicles[${index}].estimated_value`, formattedValue);
  };

  const getFieldError = (fieldName: keyof VehicleProps, index: number): string | undefined => {
    const vehicleErrors = formik.errors.vehicles as Array<Partial<Record<keyof VehicleProps, string>>> | undefined;
    if (vehicleErrors && vehicleErrors[index] && vehicleErrors[index][fieldName]) {
      return vehicleErrors[index][fieldName];
    }
    return undefined;
  };

  const handleAddVehicle = () => {
    if (formik.values.vehicles.length < 5) {
      formik.setFieldValue("vehicles", [...formik.values.vehicles, emptyVehicle]);
    }
  };

  const handleYearChange = async (e: SelectChangeEvent<string>, index: number) => {
    try {
      setIsLoading(true);
      setLoadingBrand((prev) => ({ ...prev, [index]: true }));
      setLoadingModel((prev) => ({ ...prev, [index]: true }));
      setLoadingVersion((prev) => ({ ...prev, [index]: true }));
      const { value } = e.target;
      formik.setFieldValue(`vehicles[${index}].year`, value);

      // Cargar las marcas
      const res = value ? await triggerGetVehicleByYear({ year: value }).unwrap() : [];

      // Resetear los valores de los select
      setBrandsOptions((prev) => ({ ...prev, [index]: res }));
      setModelsOptions((prev) => ({ ...prev, [index]: [] }));
      setVersionsOptions((prev) => ({ ...prev, [index]: [] }));

      // Resetear los valores de los inputs
      formik.setFieldValue(`vehicles[${index}].brand`, "");
      formik.setFieldValue(`vehicles[${index}].brand_value`, "");
      formik.setFieldValue(`vehicles[${index}].model`, "");
      formik.setFieldValue(`vehicles[${index}].model_value`, "");
      formik.setFieldValue(`vehicles[${index}].version`, "");
      formik.setFieldValue(`vehicles[${index}].version_value`, "");
    } catch (error) {
      console.error("Error al cargar marcas:", error);
      const api_error = error as ApiError;
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
      setLoadingBrand((prev) => ({ ...prev, [index]: false }));
      setLoadingModel((prev) => ({ ...prev, [index]: false }));
      setLoadingVersion((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleBrandChange = async (e: SelectChangeEvent<string>, index: number) => {
    try {
      setIsLoading(true);
      setLoadingModel((prev) => ({ ...prev, [index]: true }));
      setLoadingVersion((prev) => ({ ...prev, [index]: true }));
      const { value } = e.target;
      const { year } = formik.values.vehicles[index];

      // identificar la marca seleccionada
      const selectedBrand = brandsOptions[index]?.find((b) => b.code === value);
      formik.setFieldValue(`vehicles[${index}].brand`, value);
      formik.setFieldValue(`vehicles[${index}].brand_value`, selectedBrand?.name || "");

      // cargar los modelos
      const res = value ? await triggerGetVehicleByYearAndBrand({ year: year, make: value }).unwrap() : [];

      // resetear los valores de los select
      setModelsOptions((prev) => ({ ...prev, [index]: res }));
      setVersionsOptions((prev) => ({ ...prev, [index]: [] }));

      // resetear los valores de los inputs
      formik.setFieldValue(`vehicles[${index}].model`, "");
      formik.setFieldValue(`vehicles[${index}].model_value`, "");
      formik.setFieldValue(`vehicles[${index}].version`, "");
      formik.setFieldValue(`vehicles[${index}].version_value`, "");
    } catch (error) {
      const api_error = error as ApiError;
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
      console.error("Error al cargar marcas:", error);
    } finally {
      setIsLoading(false);
      setLoadingModel((prev) => ({ ...prev, [index]: false }));
      setLoadingVersion((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleModelChange = async (e: SelectChangeEvent<string>, index: number) => {
    try {
      setIsLoading(true);
      setLoadingVersion((prev) => ({ ...prev, [index]: true }));
      const { year, brand } = formik.values.vehicles[index];
      const { value } = e.target;

      // identificar el modelo seleccionado
      const selectedModel = modelsOptions[index]?.find((b) => b.code === value);
      formik.setFieldValue(`vehicles[${index}].model`, value);
      formik.setFieldValue(`vehicles[${index}].model_value`, selectedModel?.name || "");

      // cargar las versiones
      const res = await triggerGetVehicleByYearBrandAndModel({ year: year, make: brand, model: value }).unwrap();

      // resetear los valores de los select
      setVersionsOptions((prev) => ({ ...prev, [index]: res }));

      // resetear los valores de los inputs
      formik.setFieldValue(`vehicles[${index}].version`, "");
      formik.setFieldValue(`vehicles[${index}].version_value`, "");
    } catch (error) {
      console.error("Error al cargar el modelo:", error);
      const api_error = error as ApiError;
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
      setLoadingVersion((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleVersionChange = async (e: SelectChangeEvent<string>, index: number) => {
    const value = e.target.value;
    const selectedModel = versionsOptions[index]?.find((b) => b.code === value);
    formik.setFieldValue(`vehicles[${index}].version`, value);
    formik.setFieldValue(`vehicles[${index}].version_value`, selectedModel?.name || "");
  };

  const handleRemoveVehicleAtIndex = async (indexToRemove: number) => {
    const propertyToRemove = formik.values.vehicles[indexToRemove];

    let canDelete = true;

    // Si tiene ID, primero intenta borrar en backend
    if (propertyToRemove?.id) {
      setIsLoading(true);
      try {
        await deleteGuarantees({
          loan_application_id: application.id,
          guarantee_id: propertyToRemove.id,
        }).unwrap();
      } catch (error) {
        canDelete = false;

        const api_error = error as ApiError;

        Sentry.captureException(error, {
          extra: {
            component: "InfoGarantiaInmobiliaria",
            values: formik.values,
          },
        });

        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    }

    // Solo elimina del form si se pudo borrar
    if (canDelete) {
      const newVehicles = formik.values.vehicles.filter((_, index) => index !== indexToRemove);

      formik.setFieldValue("vehicles", newVehicles);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);

        const [response_years_list, response_garantia_data] = await Promise.all([
          triggerListVehicleYears().unwrap(),
          getGuaranteesData({ loan_application_id: application.id }).unwrap(),
        ]);
        setListYears(response_years_list);

        if (response_garantia_data.length > 0) {
          const mappedVehicles = await Promise.all(
            response_garantia_data.map(async (item, index) => {
              const type = String(item.details?.vehicle_type || "");
              const tracto = [
                "Vans y Last mile delivery",
                "Tractocamiones",
                "Camionetas",
                "Maquinaria amarilla",
                "Montacargas",
              ].includes(type);
              const isMaquinariaAmarilla = type === "Maquinaria amarilla" || type === "Montacargas";
              const year = item.details?.year?.toString() || "";
              const km = isMaquinariaAmarilla
                ? String(item.details?.estimated_mileage ?? "")
                : item.details?.estimated_mileage?.toLocaleString("en-US") || "";

              const estimatedValue = Number(item.estimated_value ?? 0);
              const estimatedValueFormatted = estimatedValue > 0 ? estimatedValue.toLocaleString("en-US") : "";

              if (tracto) {
                return {
                  type_vehicle: type,
                  year,
                  brand_value: item.details?.make || "",
                  brand: item.details?.make || "",
                  model_value: item.details?.model || "",
                  model: item.details?.model || "",
                  version: item.details?.version || "",
                  version_value: item.details?.version || "",
                  km,
                  estimated_value: estimatedValueFormatted,
                  id: item.id || undefined,
                };
              }

              let brandCatalog: VehicleStrutureCatalog[] = [];
              let modelCatalog: VehicleStrutureCatalog[] = [];
              let versionCatalog: VehicleStrutureCatalog[] = [];

              let selectedBrandCode = "";
              let selectedModelCode = "";

              if (year) {
                brandCatalog = await triggerGetVehicleByYear({ year }).unwrap();
                setBrandsOptions((prev) => ({ ...prev, [index]: brandCatalog }));

                const selectedBrand = brandCatalog.find((b) => b.name === item.details?.make);
                selectedBrandCode = selectedBrand?.code || "";

                if (selectedBrandCode) {
                  modelCatalog = await triggerGetVehicleByYearAndBrand({
                    year,
                    make: selectedBrandCode,
                  }).unwrap();
                  setModelsOptions((prev) => ({ ...prev, [index]: modelCatalog }));

                  const selectedModel = modelCatalog.find((m) => m.name === item.details?.model);
                  selectedModelCode = selectedModel?.code || "";

                  if (selectedModelCode) {
                    versionCatalog = await triggerGetVehicleByYearBrandAndModel({
                      year,
                      make: selectedBrandCode,
                      model: selectedModelCode,
                    }).unwrap();
                    setVersionsOptions((prev) => ({ ...prev, [index]: versionCatalog }));
                  }
                }
              }

              return {
                type_vehicle: type,
                year,
                brand_value: item.details?.make || "",
                brand: selectedBrandCode,
                model_value: item.details?.model || "",
                model: selectedModelCode,
                version: item.details?.car_version_code || "",
                version_value: item.details?.version || "",
                km,
                estimated_value: estimatedValueFormatted,
                id: item.id,
              };
            }),
          );

          formik.setFieldValue("vehicles", mappedVehicles);
        }
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "FormGarantiaVehicular-initial-data",
            values: formik.values,
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  return (
    <Box>
      <form onSubmit={formik.handleSubmit} noValidate>
        <FormControl
          fullWidth
          required
          sx={{
            ...customStylesAsterisk,
            mt: 2,
          }}
          error={formik.touched.guarantee_type && Boolean(formik.errors.guarantee_type)}
        >
          <InputLabel id="guarantee_type">Tipo de Garantía</InputLabel>
          <Select
            labelId="guarantee_type"
            id="guarantee_type"
            name="guarantee_type"
            fullWidth
            required
            label="Tipo de Garantía"
            value={formik.values.guarantee_type}
            onBlur={formik.handleBlur}
            onChange={(e) => {
              formik.handleChange(e);
              onSeleccion(e.target.value);
            }}
            error={formik.touched.guarantee_type && Boolean(formik.errors.guarantee_type)}
            disabled={isLoading || formik.isSubmitting || isDisabled}
          >
            <MenuItem value={""}>Selecciona</MenuItem>
            {guaranteeConfigList.map((item) => (
              <MenuItem key={item.id} value={item.guarantee_type_id}>
                {item.guarantee_type.name}
              </MenuItem>
            ))}
          </Select>
          {formik.touched.guarantee_type && formik.errors.guarantee_type && (
            <FormHelperText>{formik.errors.guarantee_type}</FormHelperText>
          )}
        </FormControl>

        <Typography
          mt={isMobileOrTablet ? 2 : 3}
          variant={isMobileOrTablet ? "body2" : "body1"}
          color="primary.dark"
          textAlign={"center"}
        >
          Detalle de Garantía {guaranteeLabel}
        </Typography>
        {formik.values.vehicles.map((vehicle: VehicleProps, index: number) => {
          const isManual = vehiculos_manual.includes(vehicle.type_vehicle);
          const isMaquinariaAmarilla = vehicle.type_vehicle === "Maquinaria amarilla" || vehicle.type_vehicle === "Montacargas";
          return (
            <Box key={index} mt={index > 0 ? 4 : 0}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id={`type_vehicle-${index}`} required>
                  Tipo de Vehículo
                </InputLabel>

                <Select
                  labelId={`type_vehicle-${index}`}
                  id={`type_vehicle-${index}`}
                  name={`vehicles[${index}].type_vehicle`}
                  fullWidth
                  required
                  label="Tipo de Vehículo"
                  value={vehicle.type_vehicle}
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue(`vehicles[${index}].type_vehicle`, value);

                    if (vehiculos_manual.includes(value)) {
                      formik.setFieldValue(`vehicles[${index}].year`, "");
                      formik.setFieldValue(`vehicles[${index}].brand`, "");
                      formik.setFieldValue(`vehicles[${index}].brand_value`, "");
                      formik.setFieldValue(`vehicles[${index}].model`, "");
                      formik.setFieldValue(`vehicles[${index}].model_value`, "");
                      formik.setFieldValue(`vehicles[${index}].version`, "");
                      formik.setFieldValue(`vehicles[${index}].version_value`, "");
                    }
                  }}
                  error={Boolean(
                    formik.touched.vehicles?.[index]?.type_vehicle && getFieldError("type_vehicle", index),
                  )}
                  disabled={isLoading}
                >
                  <MenuItem value={""}>Selecciona</MenuItem>
                  <MenuItem value={"Autos y SUVs"}>Autos y SUVs</MenuItem>
                  <MenuItem value={"Camionetas"}>Camionetas</MenuItem>
                  <MenuItem value={"Vans y Last mile delivery"}>Vans y Last mile delivery</MenuItem>
                  <MenuItem value={"Tractocamiones"}>Tractocamiones</MenuItem>
                  <MenuItem value={"Maquinaria amarilla"}>Maquinaria amarilla</MenuItem>
                  <MenuItem value={"Montacargas"}>Montacargas</MenuItem>
                </Select>
                {formik.touched.vehicles?.[index]?.type_vehicle && getFieldError("type_vehicle", index) && (
                  <FormHelperText>{getFieldError("type_vehicle", index)}</FormHelperText>
                )}
              </FormControl>

              {isManual ? (
                <TextField
                  fullWidth
                  label="Año"
                  name={`vehicles[${index}].year`}
                  value={vehicle.year}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,4}$/.test(value)) {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  id={`year-${index}`}
                  margin="normal"
                  required
                  autoComplete="off"
                  error={Boolean(formik.touched.vehicles?.[index]?.year && getFieldError("year", index))}
                  helperText={formik.touched.vehicles?.[index]?.year && getFieldError("year", index)}
                  inputProps={{ maxLength: 4 }}
                />
              ) : (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id={`year-${index}`} required>
                    Año
                  </InputLabel>

                  <Select
                    labelId={`year-${index}`}
                    id={`year-${index}`}
                    name={`vehicles[${index}].year`}
                    fullWidth
                    required
                    label="Año"
                    value={vehicle.year}
                    onBlur={formik.handleBlur}
                    onChange={(e) => handleYearChange(e, index)}
                    error={Boolean(formik.touched.vehicles?.[index]?.year && getFieldError("year", index))}
                    disabled={isLoading}
                  >
                    <MenuItem value={""}>Selecciona</MenuItem>
                    {listYears?.map((year, index) => (
                      <MenuItem key={index} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.vehicles?.[index]?.year && getFieldError("year", index) && (
                    <FormHelperText>{getFieldError("year", index)}</FormHelperText>
                  )}
                </FormControl>
              )}

              {isManual ? (
                <TextField
                  fullWidth
                  label="Marca"
                  name={`vehicles[${index}].brand`}
                  value={vehicle.brand}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue(`vehicles[${index}].brand_value`, value);
                    formik.setFieldValue(`vehicles[${index}].brand`, value);
                  }}
                  onBlur={formik.handleBlur}
                  id={`brand-${index}`}
                  margin="normal"
                  required
                  autoComplete="off"
                  error={Boolean(formik.touched.vehicles?.[index]?.brand && getFieldError("brand", index))}
                  helperText={formik.touched.vehicles?.[index]?.brand && getFieldError("brand", index)}
                />
              ) : (
                <>
                  {loadingBrand[index] ? (
                    <Skeleton variant="rectangular" height={56} sx={{ mt: 2, borderRadius: 1 }} />
                  ) : (
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel id={`brand-${index}`} required>
                        Marca
                      </InputLabel>

                      <Select
                        labelId={`brand-${index}`}
                        id={`brand-${index}`}
                        name={`vehicles[${index}].brand`}
                        fullWidth
                        required
                        label="Marca"
                        value={vehicle.brand}
                        onBlur={formik.handleBlur}
                        onChange={(e) => {
                          handleBrandChange(e, index);
                        }}
                        error={Boolean(formik.touched.vehicles?.[index]?.brand && getFieldError("brand", index))}
                        disabled={isLoading}
                      >
                        <MenuItem value={""}>Selecciona</MenuItem>
                        {brandsOptions[index]?.map((brand) => (
                          <MenuItem key={brand.code} value={brand.code}>
                            {brand.name}
                          </MenuItem>
                        ))}
                      </Select>

                      {formik.touched.vehicles?.[index]?.brand && getFieldError("brand", index) && (
                        <FormHelperText>{getFieldError("brand", index)}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </>
              )}

              {isManual ? (
                <TextField
                  fullWidth
                  label="Modelo"
                  name={`vehicles[${index}].model`}
                  value={vehicle.model}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue(`vehicles[${index}].model_value`, value);
                    formik.setFieldValue(`vehicles[${index}].model`, value);
                  }}
                  onBlur={formik.handleBlur}
                  id={`model-${index}`}
                  margin="normal"
                  required
                  autoComplete="off"
                  error={Boolean(formik.touched.vehicles?.[index]?.model && getFieldError("model", index))}
                  helperText={formik.touched.vehicles?.[index]?.model && getFieldError("model", index)}
                />
              ) : (
                <>
                  {loadingModel[index] ? (
                    <Skeleton variant="rectangular" height={56} sx={{ mt: 2, borderRadius: 1 }} />
                  ) : (
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel id={`model-${index}`} required>
                        Modelo
                      </InputLabel>

                      <Select
                        labelId={`model-${index}`}
                        id={`model-${index}`}
                        name={`vehicles[${index}].model`}
                        fullWidth
                        required
                        label="Modelo"
                        value={vehicle.model}
                        onBlur={formik.handleBlur}
                        onChange={(e) => {
                          handleModelChange(e, index);
                        }}
                        error={Boolean(formik.touched.vehicles?.[index]?.model && getFieldError("model", index))}
                        disabled={isLoading}
                      >
                        <MenuItem value={""}>Selecciona</MenuItem>
                        {modelsOptions[index]?.map((model) => (
                          <MenuItem key={model.code} value={model.code}>
                            {model.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.vehicles?.[index]?.model && getFieldError("model", index) && (
                        <FormHelperText>{getFieldError("model", index)}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </>
              )}

              {isManual ? (
                <TextField
                  fullWidth
                  label="Versión"
                  name={`vehicles[${index}].version`}
                  value={vehicle.version}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue(`vehicles[${index}].version_value`, value);
                    formik.setFieldValue(`vehicles[${index}].version`, value);
                  }}
                  onBlur={formik.handleBlur}
                  id={`version-${index}`}
                  margin="normal"
                  required
                  autoComplete="off"
                  error={Boolean(formik.touched.vehicles?.[index]?.version && getFieldError("version", index))}
                  helperText={formik.touched.vehicles?.[index]?.version && getFieldError("version", index)}
                />
              ) : (
                <>
                  {loadingVersion[index] ? (
                    <Skeleton variant="rectangular" height={56} sx={{ mt: 2, borderRadius: 1 }} />
                  ) : (
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel id={`version-${index}`} required>
                        Versión
                      </InputLabel>

                      <Select
                        labelId={`version-${index}`}
                        id={`version-${index}`}
                        name={`vehicles[${index}].version`}
                        fullWidth
                        required
                        label="Versión"
                        value={vehicle.version}
                        onBlur={formik.handleBlur}
                        onChange={(e) => {
                          handleVersionChange(e, index);
                        }}
                        error={Boolean(formik.touched.vehicles?.[index]?.version && getFieldError("version", index))}
                        disabled={isLoading}
                      >
                        <MenuItem value={""}>Selecciona</MenuItem>
                        {versionsOptions[index]?.map((version) => (
                          <MenuItem key={version.code} value={version.code}>
                            {version.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.vehicles?.[index]?.version && getFieldError("version", index) && (
                        <FormHelperText>{getFieldError("version", index)}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </>
              )}

              <FormControl fullWidth>
                <TextField
                  id={`km-${index}`}
                  name={`vehicles[${index}].km`}
                  label={isMaquinariaAmarilla ? "Horas de uso/Km" : "Km"}
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, "");
                    if (isMaquinariaAmarilla) {
                      formik.setFieldValue(`vehicles[${index}].km`, rawValue);
                      return;
                    }

                    const formattedValue = rawValue ? Number(rawValue).toLocaleString("en-US") : "";
                    formik.setFieldValue(`vehicles[${index}].km`, formattedValue);
                  }}
                  error={Boolean(formik.touched.vehicles?.[index]?.km && getFieldError("km", index))}
                  helperText={formik.touched.vehicles?.[index]?.km && getFieldError("km", index)}
                  value={vehicle.km}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{isMaquinariaAmarilla ? "Hrs/Km" : "Km"}</InputAdornment>
                    ),
                  }}
                  inputProps={{
                    maxLength: 11,
                    inputMode: "numeric",
                  }}
                  disabled={isLoading}
                />
              </FormControl>

              {isManual && (
                <FormControl fullWidth>
                  <TextField
                    id={`estimated_value-${index}`}
                    name={`vehicles[${index}].estimated_value`}
                    label="Valor Estimado"
                    margin="normal"
                    required={isManual}
                    fullWidth
                    autoComplete="off"
                    onBlur={formik.handleBlur}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeEstimatedValue(e, index)}
                    inputProps={{ maxLength: 20, inputMode: "numeric" }}
                    value={vehicle.estimated_value}
                    disabled={isLoading}
                    error={Boolean(
                      formik.touched.vehicles?.[index]?.estimated_value && getFieldError("estimated_value", index),
                    )}
                    helperText={
                      formik.touched.vehicles?.[index]?.estimated_value ? getFieldError("estimated_value", index) : ""
                    }
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </FormControl>
              )}

              {formik.values.vehicles.length > 1 && index !== 0 && (
                <Typography
                  onClick={() => handleRemoveVehicleAtIndex(index)}
                  color="primary.main"
                  fontSize={"15px"}
                  fontWeight={400}
                  textAlign={"end"}
                  sx={{ cursor: "pointer" }}
                >
                  - Eliminar Vehículo
                </Typography>
              )}
            </Box>
          );
        })}

        <Typography
          onClick={handleAddVehicle}
          color="primary.main"
          fontSize={"15px"}
          fontWeight={400}
          textAlign={"end"}
          sx={{ cursor: "pointer" }}
        >
          + Agregar otro Vehículo
        </Typography>

        <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
          <Button
            variant="default"
            type="submit"
            color="primary"
            disabled={!formik.dirty || !formik.isValid || formik.isSubmitting}
          >
            Continuar
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default FormGarantiaVehicular;
